import React, { useEffect, useState } from "react";
import { ChevronRight, CheckCircle2, FileText, Loader2 } from "lucide-react";
import PageLayout from "../components/PageLayout";
import { BRAND, BRAND_BG, BRAND_BORDER, cardBase } from "../theme/colors";
import { shpargalkaAPI } from "../api";

interface CheatItem {
  label: string;
  value: string;
}

interface CheatGroup {
  title: string;
  items: CheatItem[];
}

interface CheatCategory {
  id: string;
  title: string;
  groups: CheatGroup[];
}

interface CheatCategorySummary {
  id: string;
  title: string;
  groupsCount: number;
  itemsCount: number;
}

const ShpargalkaPage: React.FC = () => {
  const [categories, setCategories] = useState<CheatCategorySummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [selected, setSelected] = useState<CheatCategory | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [learned, setLearned] = useState<Record<string, boolean>>({});

  useEffect(() => {
    shpargalkaAPI
      .getAll()
      .then((res) => setCategories(res.data ?? []))
      .catch(() => setCategories([]))
      .finally(() => setListLoading(false));
  }, []);

  const openCategory = (id: string) => {
    setDetailLoading(true);
    shpargalkaAPI
      .getById(id)
      .then((res) => setSelected(res.data))
      .catch(() => setSelected(null))
      .finally(() => setDetailLoading(false));
  };

  const markLearned = (id: string) => {
    setLearned((prev) => ({ ...prev, [id]: true }));
    setTimeout(() => setSelected(null), 300);
  };

  if (detailLoading) {
    return (
      <PageLayout title="Shpargalka" subtitle="Asosiy raqamlar va qoidalar">
        <div
          style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <Loader2 className="animate-spin text-blue-500" size={24} />
        </div>
      </PageLayout>
    );
  }

  if (selected) {
    return (
      <PageLayout
        title={selected.title}
        subtitle="Asosiy raqamlar va qoidalar"
        onBack={() => setSelected(null)}
        backLabel="Shpargalka">
        {selected.groups.map((g, gi) => (
          <div
            key={gi}
            style={{
              ...cardBase,
              marginBottom: 12,
              overflow: "hidden",
            }}>
            <div
              style={{
                background: BRAND_BG,
                padding: "10px 14px",
                borderBottom: `1px solid ${BRAND_BORDER}`,
              }}>
              <p style={{ color: BRAND, fontWeight: 700, fontSize: 13 }}>
                {g.title}
              </p>
            </div>
            {g.items.map((item, ii) => (
              <div
                key={ii}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 14px",
                  borderBottom:
                    ii < g.items.length - 1 ?
                      "1px solid var(--border)"
                    : "none",
                }}>
                <span style={{ color: "#fff", fontSize: 13, fontWeight: 500 }}>
                  {item.label}
                </span>
                <span style={{ color: BRAND, fontSize: 13, fontWeight: 700 }}>
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        ))}

        <button
          onClick={() => markLearned(selected.id)}
          style={{
            width: "100%",
            padding: "14px",
            borderRadius: 12,
            background: learned[selected.id] ? "#22C55E" : BRAND,
            border: "none",
            color: "#fff",
            fontWeight: 700,
            fontSize: 14,
            cursor: "pointer",
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}>
          {learned[selected.id] ?
            <>
              <CheckCircle2 size={16} /> Bajarildi
            </>
          : "O'rgandim"}
        </button>
      </PageLayout>
    );
  }
  // 👇 list view
  return (
    <PageLayout title="Shpargalka" subtitle="Asosiy raqamlar va qoidalar">
      {listLoading ?
        <div
          style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <Loader2 className="animate-spin text-blue-500" size={24} />
        </div>
      : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {categories.map((cat) => {
            const isLearned = learned[cat.id];
            return (
              <button
                key={cat.id}
                onClick={() => openCategory(cat.id)}
                style={{
                  ...cardBase,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 16px",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all .2s",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = BRAND;
                  el.style.background = BRAND_BG;
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = "var(--border)";
                  el.style.background = "var(--card)";
                }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 8,
                    background: BRAND_BG,
                    border: `1px solid ${BRAND_BORDER}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}>
                  <FileText size={18} style={{ color: BRAND }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
                    {cat.title}
                  </p>
                  <p
                    style={{
                      color: "var(--muted)",
                      fontSize: 11,
                      marginTop: 2,
                    }}>
                    {cat.groupsCount} ta bo'lim
                  </p>
                </div>
                {isLearned ?
                  <CheckCircle2 size={18} style={{ color: "#22C55E" }} />
                : <ChevronRight size={16} style={{ color: "var(--muted)" }} />}
              </button>
            );
          })}
        </div>
      }
    </PageLayout>
  );
};

export default ShpargalkaPage;
